/**
 * People: who is doing what relative to their workspace. A person-by-day
 * heatmap of steps they took themselves, steps per person by domain, and a
 * table of what each person holds right now. Clicking a person opens the
 * Records tab filtered to them.
 */
import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Domain, People, Person, WindowParams, flowApi } from './flowApi';
import {
  DOMAINS, DOMAIN_COLOR, DOMAIN_LABEL, DOMAIN_MARKER, DOMAIN_NOTE, axisLine, axisTick, gridProps, tooltipProps,
} from './chartTheme';
import { ChartCard, EmptyNote, Pill, Remote, eachDay, fmtDay, fmtN, fmtWhen, useRemote } from './flowUi';
import { LivingLegend, SEQ, useChartMotion } from '../../../components/tide';
import type { LegendItem } from '../../../components/tide';

const CHART_PEOPLE = 25;

export default function PeopleView({ window: w, onSelectPerson }: {
  window: WindowParams; onSelectPerson: (userId: string, name: string) => void;
}) {
  const { state, retry } = useRemote<People>(`people|${w.from}|${w.to}`, () => flowApi.people(w));
  return <Remote state={state} retry={retry} skeleton="chart">{(data) => <PeopleBody data={data} window={w} onSelectPerson={onSelectPerson} />}</Remote>;
}

/** Shade steps 0..max into 5 bands; 0 is an empty cell, not the palest teal. */
export function heatBand(steps: number, max: number): number {
  if (steps <= 0 || max <= 0) return 0;
  return Math.min(4, Math.max(1, Math.ceil((steps / max) * 4)));
}

/** The step range a band covers, for the scale legend. */
function bandRange(band: number, max: number): string {
  if (band === 0) return '0';
  const lo = Math.floor(((band - 1) / 4) * max) + 1;
  const hi = Math.floor((band / 4) * max);
  if (hi < lo) return '';
  return lo === hi ? String(lo) : `${lo}-${hi}`;
}

function PeopleBody({ data, window: w, onSelectPerson }: {
  data: People; window: WindowParams; onSelectPerson: (userId: string, name: string) => void;
}) {
  const motion = useChartMotion();
  const [shown, setShown] = useState<string[]>(DOMAINS);
  const people = useMemo(() => [...data.people].sort((a, b) => b.steps - a.steps || a.name.localeCompare(b.name)), [data]);
  // The response echoes the window it used; fall back to the requested one.
  const from = data.window?.from || w.from;
  const to = data.window?.to || w.to;
  const days = useMemo(() => eachDay(from, to), [from, to]);
  const heat = useMemo(() => {
    const m = new Map<string, number>();
    data.heat.forEach((h) => m.set(`${h.userId}|${h.date}`, (m.get(`${h.userId}|${h.date}`) || 0) + h.steps));
    return m;
  }, [data]);
  const max = useMemo(() => Math.max(0, ...Array.from(heat.values())), [heat]);

  if (people.length === 0) return <EmptyNote>No one acted or held open work in this window.</EmptyNote>;

  const chartRows = people.slice(0, CHART_PEOPLE).map((p) => ({ name: p.name, ...p.byDomain }));
  const domainTotal = (d: Domain) => chartRows.reduce((s, r) => s + ((r as Partial<Record<Domain, number>>)[d] || 0), 0);
  const domainItems: LegendItem[] = DOMAINS.map((d) => ({
    key: d, label: DOMAIN_LABEL[d], color: DOMAIN_COLOR[d], marker: DOMAIN_MARKER[d], description: DOMAIN_NOTE[d], value: domainTotal(d), unit: 'steps',
  }));

  return (
    <div className="space-y-5">
      <ChartCard
        exposeChildren
        title="Activity by person and day"
        hint="Steps each person took themselves. Imports and automation are not counted here."
        summary={`Activity heatmap for ${people.length} people over ${days.length} days. ${people.slice(0, 10).map((p) => `${p.name}: ${p.steps} steps`).join('; ')}.`}
        foot={(
          <div className="tide-chart__foot flex flex-wrap items-center gap-2" data-testid="heat-legend">
            <span>Fewer</span>
            {SEQ.map((c, i) => (
              <span key={c} className="inline-flex items-center gap-1">
                <span aria-hidden className="inline-block h-3.5 w-3.5 rounded-[3px]" style={{ background: c, border: '1px solid var(--tide-line)' }} />
                <span className="tabular-nums">{bandRange(i, max)}</span>
              </span>
            ))}
            <span>More (busiest cell: {fmtN(max)})</span>
          </div>
        )}
      >
        <div className="tide-scroll" tabIndex={0} role="region" aria-label="Activity by person and day (scrolls sideways)">
          <div
            role="grid"
            aria-label="Steps per person per day"
            className="grid gap-px text-xs"
            style={{ gridTemplateColumns: `minmax(8rem, 12rem) repeat(${days.length}, minmax(14px, 1fr))`, minWidth: `${128 + days.length * 16}px` }}
          >
            <div role="row" className="contents">
              <div role="columnheader" className="sticky left-0 pr-2 tide-ink-2" style={{ background: 'var(--tide-surface)' }}>Person</div>
              {days.map((d, i) => (
                <div key={d} role="columnheader" className="text-center tide-ink-2" style={{ fontSize: 12 }}>{i % 5 === 0 || days.length <= 14 ? fmtDay(d) : <span className="tide-sr">{fmtDay(d)}</span>}</div>
              ))}
            </div>
            {people.map((p) => (
              <div key={p.userId} role="row" className="contents">
                <div role="rowheader" className="sticky left-0 truncate pr-2" style={{ background: 'var(--tide-surface)' }} title={p.name}>{p.name}</div>
                {days.map((d) => {
                  const n = heat.get(`${p.userId}|${d}`) || 0;
                  const band = heatBand(n, max);
                  return (
                    <div
                      key={d}
                      role="gridcell"
                      data-testid="heat-cell"
                      title={`${p.name}, ${d}: ${n} ${n === 1 ? 'step' : 'steps'}`}
                      className="h-5 rounded-[3px]"
                      style={{ background: SEQ[band], border: '1px solid var(--tide-line)' }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </ChartCard>

      <ChartCard
        side
        title="Steps per person by domain"
        hint={people.length > CHART_PEOPLE ? `The ${CHART_PEOPLE} busiest of ${people.length} people; the table lists everyone.` : undefined}
        summary={`Steps per person by domain. ${chartRows.slice(0, 10).map((r) => r.name).join(', ')}.`}
        legend={(
          <LivingLegend
            stacked
            density="compact"
            title="Domains"
            mode="toggle"
            items={domainItems}
            selected={shown}
            onChange={setShown}
            scope="Shows or hides a domain in every person's bar. The table below does not change."
          />
        )}
      >
        <ResponsiveContainer width="100%" height="100%" minHeight={Math.max(280, chartRows.length * 28 + 60)}>
          <BarChart data={chartRows} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid {...gridProps} horizontal={false} />
            <XAxis type="number" tick={axisTick} axisLine={axisLine} tickLine={false} allowDecimals={false} />
            <YAxis type="category" dataKey="name" tick={axisTick} axisLine={false} tickLine={false} width={140} interval={0} />
            <Tooltip {...tooltipProps} />
            {DOMAINS.map((d) => (
              <Bar key={d} dataKey={d} name={DOMAIN_LABEL[d]} stackId="p" fill={DOMAIN_COLOR[d]} stroke="var(--tide-surface)" strokeWidth={1} hide={!shown.includes(d)} {...motion} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <section className="tide-panel">
        <header className="tide-panel__head">
          <h2 className="tide-panel__title">Who holds what</h2>
          <p className="tide-panel__hint">Open held is right now (cases assigned, UMED cases operated, Finance handoffs accepted). Choose a person to see their steps.</p>
        </header>
        <div className="tide-scroll" tabIndex={0} role="region" aria-label="Who holds what (scrolls sideways)">
          <table className="tide-table min-w-[860px]">
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Workspaces</th>
                <th scope="col" className="tide-num">Steps</th>
                <th scope="col" className="tide-num">Open held</th>
                <th scope="col" className="tide-num">Oldest held (days)</th>
                <th scope="col" className="tide-num">Send-backs received / made</th>
                <th scope="col">Last active</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => <PersonRow key={p.userId} p={p} onSelect={onSelectPerson} />)}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function PersonRow({ p, onSelect }: { p: Person; onSelect: (userId: string, name: string) => void }) {
  return (
    <tr>
      <td>
        <button type="button" onClick={() => onSelect(p.userId, p.name)} className="tide-link" aria-label={`Show steps by ${p.name}`}>
          {p.name}
        </button>
        {p.email && <div className="tide-tiny tide-ink-2">{p.email}</div>}
      </td>
      <td>
        {p.workspaces.length === 0 ? <span className="tide-tiny tide-ink-2">none recorded</span> : (
          <span className="flex flex-wrap gap-1">{p.workspaces.map((ws) => <Pill key={ws} tone="neutral">{ws.replace(/_/g, ' ').toLowerCase()}</Pill>)}</span>
        )}
      </td>
      <td className="tide-num tabular-nums">{fmtN(p.steps)}</td>
      <td className="tide-num tabular-nums">{fmtN(p.openHeld)}</td>
      <td className="tide-num tabular-nums">{p.oldestHeldDays == null ? '-' : fmtN(p.oldestHeldDays)}</td>
      <td className="tide-num tabular-nums">{p.sendBacksReceived == null ? <span title="not recorded yet">&mdash;</span> : fmtN(p.sendBacksReceived)} / {fmtN(p.sendBacksMade)}</td>
      <td className="tide-tiny">{fmtWhen(p.lastStepAt)}</td>
    </tr>
  );
}
